import { action, computed, extendObservable, observable, runInAction, makeObservable } from "mobx";
import ClassesApi from "../api/ClassesApi";
import ProcessoApi from "../api/ProcessoApi";
import { IEtiqueta, IUsuario } from "../modelo";
import { IAutocomplete } from "../modelo/Autocomplete";
import { ClasseJudicialDto } from "../modelo/dto/ClasseJudicialDto";
import { ISearch } from "../modelo/IBuscaProcessos";
import { UnidadePublicadora } from "../modelo/UnidadePublicadora";
import ServidorService from "../services/ServidorService";
import ProcessoStore from "./ProcessoStore";
import Store from "./Store";
import UsuarioStore from "./UsuarioStore";

const abreviacao = (s) => {
    return /^([A-Z]\.)+$/.test(s);
}

const numeralRomano = (s) => {
    return /^M{0,4}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/.test(s);
}

const capitalize = (texto) => {
    const prepos = ["da", "do", "das", "dos", "a", "e", "de"];
    return texto?.split(' ')
        .map((palavra) => {
            if (abreviacao(palavra) || numeralRomano(palavra)) {
                return palavra;
            }

            palavra = palavra.toLowerCase();
            if (prepos.includes(palavra)) {
                return palavra;
            }
            return palavra.charAt(0).toUpperCase() + palavra.slice(1);
        })
        .join(' ');
}

class GabineteStore extends Store {
    listaUsuariosGabinete: IUsuario[] = [];
    listaUsuariosOrdenadoGabinete: IUsuario[] = [];
    etiquetas: IEtiqueta[] = [];
    etiquetasParaAutoComplete: IAutocomplete<string>[] = [];
    abrirEscaninho = false;
    classes: ClasseJudicialDto[] = [];
    unidadesDePublicacao: UnidadePublicadora[] = [];
    search: ISearch = { paginacao: { pagina: 1 } } as ISearch;
    servidoresCDS = [];
    nomeServidoresCache: Map<string, string> = new Map();
    carregandoUsuariosGabinete = false;
    descricaoGabinetePorSigla: Record<string, string> = {};
    private requisicoesDescricaoGabinete = new Map<string, Promise<void>>();
    private readonly maxTentativasDescricaoGabinete = 3;
    ultimaAtualizacaoEnvelope;

    constructor() {
        super();
        makeObservable(this, {
            listaUsuariosGabinete: observable,
            listaUsuariosOrdenadoGabinete: observable,
            etiquetas: observable,
            etiquetasParaAutoComplete: observable,
            abrirEscaninho: observable,
            classes: observable,
            unidadesDePublicacao: observable,
            search: observable,
            servidoresCDS: observable,
            nomeServidoresCache: observable,
            carregandoUsuariosGabinete: observable,
            descricaoGabinetePorSigla: observable,
            ultimaAtualizacaoEnvelope: observable,
            objetoSave: computed,
            descricaoGabineteAtual: computed,
            adicionarObservable: action
        });
    }

    get objetoSave() {
        return {
            nomeStore: 'GabineteStore',
            listaUsuariosGabinete: this.listaUsuariosGabinete,
            descricaoGabinetePorSigla: this.descricaoGabinetePorSigla
        };
    }

    get descricaoGabineteAtual() {
        const sigla = (UsuarioStore.gabinete || '').trim();
        return (sigla && this.descricaoGabinetePorSigla?.[sigla]) || '';
    }

    carregarDescricaoGabineteAtual = async (gabinete?: string, tentativaAtual = 1) => {
        const sigla = (gabinete || UsuarioStore.gabinete || '').trim();
        if (!UsuarioStore.token || !sigla) {
            return;
        }

        if (this.descricaoGabinetePorSigla?.[sigla]) {
            return;
        }

        if (this.usarBuscaPorDescricaoLocalizacao(sigla)) {
            runInAction(() => {
                this.descricaoGabinetePorSigla = {
                    ...this.descricaoGabinetePorSigla,
                    [sigla]: this.descricaoLocalizacaoPorSigla(sigla)
                };
            });
            return;
        }

        const requisicaoEmAndamento = this.requisicoesDescricaoGabinete.get(sigla);
        if (requisicaoEmAndamento) {
            await requisicaoEmAndamento;
            return;
        }

        const requisicao = (async () => {
            try {
                const resultado = await ServidorService.buscarOrgaoJulgador(sigla);
                const gabineteAtual = Array.isArray(resultado) ? resultado[0] : resultado;
                const descricao = gabineteAtual?.descricao || sigla;

                runInAction(() => {
                    this.descricaoGabinetePorSigla = {
                        ...this.descricaoGabinetePorSigla,
                        [sigla]: descricao
                    };
                });
            } catch (erro) {
                if (tentativaAtual < this.maxTentativasDescricaoGabinete) {
                    await new Promise(resolve => setTimeout(resolve, 250));
                    await this.carregarDescricaoGabineteAtual(sigla, tentativaAtual + 1);
                    return;
                }
                throw erro;
            }
        })();

        this.requisicoesDescricaoGabinete.set(sigla, requisicao);
        try {
            await requisicao;
        } finally {
            this.requisicoesDescricaoGabinete.delete(sigla);
        }
    }

    async inicializar() {
        // Preserva descrições já carregadas via API antes do restore do storage
        const descricaoPreExistente = { ...(this.descricaoGabinetePorSigla || {}) };
        super.inicializar();
        // Mescla de volta para não apagar valor carregado pela API antes do storage restore
        runInAction(() => {
            this.descricaoGabinetePorSigla = {
                ...(this.descricaoGabinetePorSigla || {}),
                ...descricaoPreExistente,
            };
        });
        // Garante carga via API; aguarda para que o valor esteja disponível ao retornar
        await this.carregarDescricaoGabineteAtual().catch(() => {});

        await this.atualizarUsuariosGabinete();
        const listaUsuariosOrdenado = await this.recuperarMinistrosServidoresOrdenados();
        const favoritos = await ProcessoApi.recuperarProcessosFavoritos();
        const classes = await ClassesApi.recuperarDescricaoDeClasses();
        const unPub = await ProcessoApi.carregarTodasUnidadesPublicacao();
        runInAction(() => {
            UsuarioStore.processosFavoritosUsuario = favoritos
            this.unidadesDePublicacao = unPub;
            this.classes = classes;
            this.listaUsuariosOrdenadoGabinete = listaUsuariosOrdenado;
            setTimeout(() => {
                runInAction(() => {
                    this.ultimaAtualizacaoEnvelope = new Date();
                });
            }, 10000)
        });
    }

    adicionarObservable(objeto, propriedade) {
        if (!Object.prototype.hasOwnProperty.call(objeto, propriedade)) {
            extendObservable(objeto, {
                [propriedade]: undefined
            });
        }
    }

    usarBuscaPorDescricaoLocalizacao = (siglaGabinete: string): boolean => {
        return /^SETR[1-8]$/.test(siglaGabinete) || ['SEPREX', 'SEGJUD'].includes(siglaGabinete);
    }

    descricaoLocalizacaoPorSigla = (siglaGabinete: string): string => {
        if (/^SETR[1-8]$/.test(siglaGabinete)) {
            const numeroTurma = siglaGabinete.replace('SETR', '');
            return `${numeroTurma}ª Turma`;
        }

        const mapeamento = {
            SEGJUD: 'SEGJUD',
            SEPREX: 'SEPREX'
        };

        return mapeamento[siglaGabinete] || siglaGabinete;
    }

    atualizarUsuariosGabinete = async (gabinete?: string) => {
        const usuario = UsuarioStore;
        const siglaGabinete = gabinete || usuario?.localizacao?.siglaOrgaoJulgador;

        if (!siglaGabinete) {
            runInAction(() => {
                this.carregandoUsuariosGabinete = false;
            });
            return []
        }

        runInAction(() => {
            this.carregandoUsuariosGabinete = true;
        });

        try {
            let servidores = [];
            if (this.usarBuscaPorDescricaoLocalizacao(siglaGabinete)) {
                const descricaoLocalizacao = this.descricaoLocalizacaoPorSigla(siglaGabinete);
                servidores = await ServidorService.buscarServidoresPJeSecretarias(descricaoLocalizacao);
            } else {
                servidores = await ServidorService.buscarServidoresPJe(siglaGabinete);
            }

            const cpfs = servidores.map(serv => serv.login);
            const dadosServidor = await ServidorService.msConsultaUsuarios(cpfs);
            const dadosServidorPorCpf = dadosServidor || {};
            const servidoresAtualizados = servidores.map((servidor) => {
                servidor.dadosServidor = dadosServidorPorCpf[servidor.login];
                return servidor;
            });

            runInAction(() => {
                this.listaUsuariosGabinete = servidoresAtualizados.filter((s) =>
                    s.dadosServidor?.servidor?.lotacao !== 'CDS'
                );
                // Evita manter no cache CPFs gravados antes do carregamento dos usuários.
                this.limparCacheNomes();
            });
        } finally {
            runInAction(() => {
                this.carregandoUsuariosGabinete = false;
            });
        }

        return []
    }

    recuperarNomesResponsaveis = (minutas) => {
        minutas.map((minuta) => {
            minuta.nomeResponsavel = this.recuperarNomeServidor(minuta.cpfResponsavel);
            minuta.nomeUltimaAlteracao = this.recuperarNomeServidor(minuta.cpfUltimaAlteracao);
            const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso == minuta.idProcesso);
            if (processo.numeroCpfResponsavel) {
                minuta.nomeResponsavel = this.recuperarNomeServidor(processo.numeroCpfResponsavel);
            }
        });
        return minutas;
    }

    recuperarNomeServidor = (cpf: string, lista?: any[], nomeLogado?: string): string => {
        if (cpf === UsuarioStore.usuario?.login) {
            return capitalize(nomeLogado) || capitalize(UsuarioStore.usuario?.nome) || capitalize(UsuarioStore.usuario?.login)
        }

        if (!cpf) return '';

        if (this.nomeServidoresCache.has(cpf)) {
            return this.nomeServidoresCache.get(cpf)!;
        }

        const origem = lista || this.listaUsuariosGabinete;
        const usuario = origem.find(usr => usr.login === cpf);

        const resultado = (usuario && capitalize(usuario.nome)) || (cpf.trim().padStart(11, '0'));

        // Só cacheia fallback de CPF quando já existe lista carregada;
        // caso contrário, evita "congelar" o CPF antes de obter o nome real.
        if (usuario || origem.length > 0) {
            this.nomeServidoresCache.set(cpf, resultado);
        }

        return resultado;
    }

    deveAguardarNomeServidor = (cpf: string, lista?: any[]): boolean => {
        if (!cpf) {
            return false;
        }

        const cpfNormalizado = cpf.toString().trim().padStart(11, '0');
        if (this.nomeServidoresCache.has(cpfNormalizado) || this.nomeServidoresCache.has(cpf)) {
            return false;
        }

        const origem = lista || this.listaUsuariosGabinete;
        const usuario = origem.find(usr => usr.login === cpfNormalizado);
        return !usuario && origem.length === 0 && this.carregandoUsuariosGabinete;
    }

    limparCacheNomes = () => {
        this.nomeServidoresCache.clear();
    }

    recuperarNomeServidorComTitleCase = (servidor) => {
        if (Object.prototype.hasOwnProperty.call(servidor, 'nome')) {
            return capitalize(servidor.nome);
        } else {
            const usuario = this.listaUsuariosGabinete.find(usr => usr.login === servidor.login);
            return usuario && capitalize(usuario.nome);
        }
    }

    isMinistro(servidor: IUsuario) {
        return servidor.localizacoes.every(l => l.papel.identificador === 'magistrado');
    }

    /*
        Retrona servidores na ordem: 1-Mininistros, 2-Magistrado, 3-Servidores
    **/
    recuperarMinistrosServidoresOrdenados = () => {
        const servidores = [];
        const ministros = [];

        this.listaUsuariosGabinete.forEach((servidor, _index) => {

            if (servidor.magistrado) {
                this.isMinistro(servidor) ? ministros.push(servidor) : servidores.unshift(servidor);
            } else {
                servidores.push(servidor);
            }
        });
        ministros.length > 0 && servidores.unshift(...ministros);
        return servidores;
    }


}

export default new GabineteStore();
